import axios, { AxiosError, AxiosResponse } from 'axios';
import { getLogger } from '../diagnostics/logger';
import queryString from 'query-string';
import { getSimManagementErrorFromAxiosError } from '.';
import config from '../config';
import { QueryResult } from '../models/queryResult';
import { SimSubmission } from '../models/transfer/input/SimSubmission';
import { Filter } from './filters';
import { AssignAlertManagerRequest } from './models/AssignAlertManagerRequest';
import { BundleBucketRequest } from './models/BundleBucketRequest';
import { ConfigureBarServiceConfigPayload } from './models/ConfigureBarServiceConfigPayload';
import { IExportEsimRequest } from './models/IExportEsimRequest';
import { IImeiLockRequest } from './models/IImeiLockRequest';
import { ISendSmsRequest } from './models/ISendSmsRequest';
import { ISubmissionResponse } from './models/ISubmissionResponse';
import { MigrateInSubmissionRequest } from './models/MigrateInSubmissionRequest';
import { QueryOptions } from './models/QueryOptions';
import { RemoveAlertManagerRequest } from './models/RemoveAlertManagerRequest';
import { SubmissionResponseData } from './models/SubmissionResponseData';
import { SubmissionSearchOptions } from './models/SubmissionSearchOptions';
import getTokeniser from './tokeniser';
import { BundleAlertDefinition } from '../models/transfer/input/BundleAlertDefinition';

const log = getLogger('simManagement.api.simSubmissionApi');

export function create(data: any): Promise<any> {
  let payload = data;
  let urlPath = '';

  if (data.commands) {
    const commandType = getCommandType(data);
    switch (commandType) {
      case 'provision':
        payload = { ...data.commands[0].commandOptions, actionDate: data.actionDate };
        urlPath = '/Provision';
        break;
      case 'change-provision':
        payload = {
          ...data.commands[0].commandOptions,
          name: data.name,
          actionDate: data.actionDate
        };
        urlPath = '/ChangeProvision';
        break;
      case 'migrate-in': {
        const command = data.commands[0];
        const { commandOptions, serialNumbers } = command;
        payload = {
          name: data.name,
          stock: serialNumbers.map((serialNumber: string, index: number) => ({
            serialNumber,
            msisdn: commandOptions.tels[index]
          })),
          tariffNumber: commandOptions.tariffNumber,
          boltOns: (commandOptions.boltOns || []).map((boltOn: string | number) => Number(boltOn)),
          provisioningOrderNumber: commandOptions.provisioningOrderNumber || null,
          createNewProvisioningOrder: commandOptions.createNewProvisioningOrder,
          newProvisioningOrderName: commandOptions.newProvisioningOrderName || null,
          keepSim: commandOptions.keepSim === null ? false : commandOptions.keepSim
        } as MigrateInSubmissionRequest;
        urlPath = '/MigrateIn';
        break;
      }
      case 'unprovision':
        payload = {
          serialNumbers: data.commands[0].commandOptions.serialNumbers || data.commands[0].serialNumbers || [],
          actionDate: data.actionDate
        };
        urlPath = '/unprovision';
        break;
      case 'configure-spend-cap':
        payload = data.commands[0].commandOptions;
        urlPath = '/ConfigureSpendCap';
        break;
      case 'remove-spend-cap':
        payload = data.commands[0].commandOptions.serialNumbers;
        urlPath = '/RemoveSpendCap';
        break;
      case 'export-esim':
        payload = {
          ...data.commands[0],
          ...data.commands[0].commandOptions
        };
        urlPath = '/ExportEsim';
        break;
      case 'configure-bundle-control':
        payload = data.commands[0].commandOptions;
        urlPath = '/ConfigureBundleControl';
        break;
      case 'configure-fair-usage': {
        const configureFairUsageOptions = data.commands[0].commandOptions;
        payload = {
          buckets: configureFairUsageOptions.buckets,
          serialNumber: data.commands[0].serialNumbers[0],
          name: data.name
        };
        urlPath = '/ConfigureFairUsage';
        break;
      }
      case 'top-up':
        payload = {
          serialNumbers: data.commands[0].serialNumbers,
          amountMb: data.commands[0].commandOptions.AmountMb,
          name: data.name
        };
        urlPath = '/TopUp';
        break;
      case 'remove-bundle-control':
        data.commands[0].commandOptions.actionDate = data.actionDate;
        data.commands[0].commandOptions.serialNumbers = data.commands[0].serialNumbers;
        data.commands[0].commandOptions.name = data.name;
        payload = {
          ...data.commands[0].commandOptions
        };
        urlPath = '/RemoveBundleControl';
        break;
      case 'configure-threshold':
        payload = {
          ...data.commands[0].commandOptions,
          serialNumbers: data.commands[0].serialNumbers,
          name: data.name
        };
        urlPath = '/ConfigureThreshold';
        break;
      case 'lift-spend-cap':
        payload = {
          ...data.commands[0].commandOptions,
          serialNumbers: data.commands[0].serialNumbers
        };
        urlPath = '/LiftSpendCap';
        break;
      case 'configure-sms-alerts':
        payload = {
          ...data.commands[0].commandOptions,
          serialNumbers: data.commands[0].serialNumbers,
          name: data.name
        };
        urlPath = '/ConfigureAlert';
        break;
      case 'remove-sms-alerts':
        payload = data.commands[0].serialNumbers || data.commands[0].commandOptions.serialNumbers || [];
        urlPath = '/RemoveAlert';
        break;
      case 'send-sms':
        payload = data.commands[0].commandOptions;
        urlPath = '/SendSms';
        break;
      case 'sim-swap':
        payload = {
          swaps: data.commands[0].serialNumbers.map((serialNumber: string, index: number) => ({
            serialNumber,
            swapSerialNumber: data.commands[0].commandOptions.simSwaps[index]
          })),
          name: data.name
        };
        urlPath = '/SimSwap';
        break;
      case 'edit-po':
        payload = {
          serialNumbers: data.commands[0].serialNumbers,
          order1: data.commands[0].commandOptions.order1,
          order2: data.commands[0].commandOptions.order2,
          order3: data.commands[0].commandOptions.order3,
          order4: data.commands[0].commandOptions.order4,
          order5: data.commands[0].commandOptions.order5
        };
        urlPath = '/EditOrderNotes';
        break;
      default:
        break;
    }
  }

  return new Promise<string>((resolve, reject) => {
    const apiPath = urlPath === '/ConfigureFairUsage' ? 'api/SimSubmission' : 'api/simsubmission';
    const url = `${config.API_BASE_URL}${apiPath}${urlPath}`;

    axios
      .post(url, payload)
      .then((response: AxiosResponse<SubmissionResponseData>) => {
        const submissionId = response.data.ticketNumber;
        resolve(submissionId);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to submit commands.', simManagementError);
        reject(simManagementError);
      });
  });
}

function getCommandType(data: any): string | undefined {
  if (data && data.commands && data.commands[0] && data.commands[0].commandOptions) {
    return data.commands[0].commandOptions.type;
  }
  return undefined;
}

/**
 * Requests the cancellation of a submission on the server.
 * @param ticketNumber The ticket number of the submission to cancel.
 * @param reason The reason for the cancellation.
 */
export function cancel(ticketNumber: string, reason: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const url = `${config.API_BASE_URL}api/simSubmission/${ticketNumber}`;
    axios
      .delete(url, {
        params: {
          reason
        }
      })
      .then((response: any) => {
        resolve();
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to cancel submission ${ticketNumber}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function complete(
  ticketNumber: string,
  status: 'complete' | 'cancelled',
  reason?: string,
  msisdns?: { [serial: string]: string },
  serialNumbers?: { [serial: string]: string },
  lpaStrings?: { [serial: string]: string }
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const url = `${config.API_BASE_URL}api/simSubmission/${ticketNumber}/Complete`;
    axios
      .post(url, { status, reason, msisdns, serialNumbers, lpaStrings })
      .then(() => {
        resolve();
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to manually complete submission ${ticketNumber}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function getByTicketNumber(ticketNumber: string, includeCommands: boolean): Promise<SimSubmission> {
  const url = `${config.API_BASE_URL}api/simSubmission/${ticketNumber}?includeCommands=${includeCommands}`;
  return new Promise<SimSubmission>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = response.data as SimSubmission;
        result.commands.forEach((c) =>
          c.stocks.forEach((s) => {
            if (s.job && s.job.result && s.job.result.type === 'orange-usage') {
              s.job.result.serialNumber = s.serialNumber;
              s.job.result.tel = s.tel || '';
            }
          })
        );
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to load submission ${ticketNumber}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function exportToFile(ticketNumber: string): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    axios({
      url: `${config.API_BASE_URL}api/simSubmission/${ticketNumber}/download`,
      method: 'GET',
      responseType: 'blob'
    })
      .then((response: any) => {
        const blob = new Blob([response.data]);
        resolve(blob);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to export submission ${ticketNumber}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

const defaultSearchOptions: SubmissionSearchOptions = {
  filters: {},
  sortModel: [],
  isScrollDown: false,
  searchTerm: ' ',
  skip: 0,
  take: 50,
  wrapWith: {
    totalCount: false
  }
};

function tokeniseCondition(item: Filter, property: string): { key: string; value: any }[] {
  const { type, filter, filterType } = item;
  const tokeniser = getTokeniser(filterType);
  if (tokeniser) {
    return tokeniser.tokeniseCondition(item, property);
  }

  return [];
}

export function get(options: Partial<SubmissionSearchOptions>): Promise<QueryResult<SimSubmission>> {
  const resolvedOptions: SubmissionSearchOptions = Object.assign(defaultSearchOptions, options);
  const { filters, sortModel } = options;
  const queryOptions: QueryOptions = {};

  if (resolvedOptions.companyId) {
    queryOptions.companyId = resolvedOptions.companyId;
  }

  if (resolvedOptions.accountId) {
    queryOptions.accountId = resolvedOptions.accountId;
  }

  queryOptions.searchTerm = resolvedOptions.searchTerm ? resolvedOptions.searchTerm : ' ';

  if (resolvedOptions.searchType) {
    queryOptions.searchType = resolvedOptions.searchType;
  }

  if (!options.isScrollDown) {
    resolvedOptions.skip = 0;
  }

  let filterCount = 0;
  if (filters && filters.length) {
    filters.forEach((filter: any) => {
      let searchField = '';
      if (filter.filterType == 'number') {
        if (filterCount > 0) {
          searchField = '&' + filter.id;
        } else {
          searchField = filter.id;
        }
      } else if (filter.filterType == 'date') {
        if (filterCount > 0) {
          searchField = '&' + filter.id + '>';
        } else {
          searchField = filter.id + '>';
        }
      } else {
        if (filterCount > 0) {
          searchField = '&' + filter.id;
        } else {
          searchField = filter.id;
        }
      }
      queryOptions[searchField] = filter.value;
    });
    filterCount++;
  }

  if (sortModel && sortModel.length) {
    sortModel.forEach((value) => {
      const orderByKey = value.desc ? 'orderByDesc' : 'orderBy';
      queryOptions[orderByKey] = value.id;
    });
  }

  if (resolvedOptions.skip) {
    queryOptions.skip = resolvedOptions.skip;
  }

  if (resolvedOptions.take) {
    queryOptions.take = resolvedOptions.take;
  }

  const queryParams = queryString.stringify(queryOptions);
  let url = config.API_BASE_URL + `api/simSubmission?${queryParams}`;

  // remove equals sign after < or >
  url = url.replace('%3E=', '%3E').replace('%3C=', '%3C');

  return new Promise<QueryResult<SimSubmission>>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<QueryResult<SimSubmission>>) => {
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load submission list.', simManagementError);
        reject(simManagementError);
      });
  });
}

export function createImeiLock(imeiLockRequest: IImeiLockRequest) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/CreateImeiLock`, imeiLockRequest)
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function removeImeiLock(serialNumbers: Array<string>) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/RemoveImeiLock`, { serialNumbers })
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function refreshConnection(serialNumbers: Array<string>, clearLocation: boolean = false) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(
        `${config.API_BASE_URL}api/simSubmission/SimRefresh`,
        clearLocation ? { serialNumbers, clearLocation } : { serialNumbers }
      )
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function resetImeiLock(serialNumbers: Array<string>, unsuspend: boolean = true) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/ResetImeiLock`, { serialNumbers, unsuspend })
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function sendSms(request: ISendSmsRequest) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/SendSms`, request)
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function exportEsim(request: IExportEsimRequest) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/ExportEsim`, request) // URL and request interface may need to change
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function bundleUsage(serials: string[]): Promise<string> {
  const url = `${config.API_BASE_URL}api/simSubmission/BundleUsage`;

  return new Promise<string>((resolve, reject) => {
    axios
      .post(url, serials)
      .then((reply: AxiosResponse<ISubmissionResponse>) => {
        resolve(reply.data.ticketNumber);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to request bundle usage`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function ConfigureBundleBuckets(request: BundleBucketRequest) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/ConfigureFairUsage`, request)
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);

        reject(simManagementError.message);
      });
  });
}

export function assignAlertManager(request: AssignAlertManagerRequest) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/AssignAlertManager`, request)
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);

        reject(simManagementError.message);
      });
  });
}

export function removeAlertManager(request: RemoveAlertManagerRequest) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/RemoveAlertManager`, request)
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);

        reject(simManagementError.message);
      });
  });
}

//sms Alerts
export function ConfigureSmsAlerts(request: BundleAlertDefinition) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simsubmission/ConfigureAlert`, request)
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);

        reject(simManagementError.message);
      });
  });
}

export function RemoveSmsAlert(request: BundleAlertDefinition) {
  return new Promise<ISubmissionResponse>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simsubmission/RemoveAlert`, request)
      .then((response: AxiosResponse) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);

        reject(simManagementError.message);
      });
  });
}

export function configureBarServiceConfig(payload: ConfigureBarServiceConfigPayload) {
  return new Promise<string>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/simSubmission/BarServiceConfig`, payload)
      .then((response: AxiosResponse<SubmissionResponseData>) => {
        const submissionId = response.data.ticketNumber;
        resolve(submissionId);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        reject(simManagementError);
      });
  });
}
